import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import MobileFrame from "../../../components/MobileFrame";
import {
    ChevronLeft,
    Mail,
    Pencil,
    CheckCircle2,
    Loader2,
    AlertCircle,
} from "lucide-react";
import contractAnimation from "../../../assets/contract.json";
import Email from "../../../assets/Email.json";
import {
    sendEmailOTP,
    verifyEmailOTP,
    verifyGoogleEmail,
} from "../../../api/email.api";
import {
    journeyAction,
    journeyStatus,
} from "../../../api/journey.api";
import { useDispatch, useSelector } from "react-redux";
import { setJourney } from "../../../store/journeySlice";
import type { RootState } from "../../../store";
import AgreementPdfViewer from "../../../components/AgreementPdfViewer";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";

type Step =
    | "emailEntry"
    | "emailOtp"
    | "emailVerifying"
    | "pdfView"
    | "otpVerify"
    | "verifying"
    | "signed";

interface AgreementSigningProps {
    onBack: () => void;
    onDone: () => void;
    agreementPdfBase64?: string;
}

const OTP_LENGTH = 6;
const RESEND_WAIT_SECONDS = 60;

/**
 * Only expose controlled API messages to the user.
 * Never fall back to raw Axios/network errors.
 */
const getErrorMessage = (
    error: unknown,
    fallback: string
): string => {
    if (error && typeof error === "object") {
        const anyErr = error as any;

        const apiMessage =
            anyErr?.response?.data?.message ||
            anyErr?.response?.data?.error;

        if (
            typeof apiMessage === "string" &&
            apiMessage.trim()
        ) {
            return apiMessage.trim();
        }
    }

    return fallback;
};

const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const PoweredByFooter = ({
    className = "",
}: {
    className?: string;
}) => (
    <p
        className={`text-center text-[11px] font-semibold tracking-wide text-gray-400 ${className}`}
    >
        CleverPe
    </p>
);

const InlineError = ({
    message,
}: {
    message: string | null;
}) => {
    if (!message) return null;

    return (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />

            <p className="text-[12px] leading-snug text-red-600">
                {message}
            </p>
        </div>
    );
};

const STEP_TITLES: Record<Step, string> = {
    emailEntry: "Email Verification",
    emailOtp: "Email Verification",
    emailVerifying: "Email Verification",
    pdfView: "Loan Agreement",
    otpVerify: "Sign Agreement",
    verifying: "Sign Agreement",
    signed: "Sign Agreement",
};

const AgreementSigning = ({
    onBack,
    onDone,
    agreementPdfBase64,
}: AgreementSigningProps) => {
    const dispatch = useDispatch();

    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );

    const customerMobileMasked =
        journey?.customerMobileMasked ?? "";

    /*
      * =========================================================
      * RESUME
      * =========================================================
      *
      * If email was already verified in a previous session,
      * skip straight to the PDF view instead of forcing the
      * customer to re-verify their email.
      */
    const agreementResume =
        journey?.resumeMetadata?.AGREEMENT;

    const getInitialStep = (): Step => {
        if (agreementResume?.emailVerified) {
            return "pdfView";
        }
        return "emailEntry";
    };

    const [step, setStep] =
        useState<Step>(getInitialStep);

    /* =========================================================
       Agreement PDF
       ========================================================= */

    const [agreementPdf, setAgreementPdf] =
        useState<string | null>(
            agreementPdfBase64 ?? null
        );

    const [agreementLoadError, setAgreementLoadError] =
        useState(false);

    const [isLoadingAgreement, setIsLoadingAgreement] =
        useState(false);

    /* =========================================================
       Email Verification (OTP)
       ========================================================= */

    const [email, setEmail] = useState("");

    const [isEmailLocked, setIsEmailLocked] =
        useState(false);

    const [emailOtpHash, setEmailOtpHash] =
        useState<string | null>(null);

    const [emailOtp, setEmailOtp] = useState<string[]>(
        Array(OTP_LENGTH).fill("")
    );

    const [emailResendTimer, setEmailResendTimer] =
        useState(0);

    const [isSendingEmailOtp, setIsSendingEmailOtp] =
        useState(false);

    const [emailEntryError, setEmailEntryError] =
        useState<string | null>(null);

    const [emailOtpError, setEmailOtpError] =
        useState<string | null>(null);

    const [emailResendError, setEmailResendError] =
        useState<string | null>(null);

    const emailInputRef =
        useRef<HTMLInputElement | null>(null);

    const emailOtpRefs =
        useRef<(HTMLInputElement | null)[]>([]);

    /*
     * Tracks the last email OTP value that was
     * auto-submitted, so the auto-verify effect
     * doesn't resubmit the same value repeatedly.
     */
    const lastAutoSubmittedEmailOtpRef =
        useRef<string>("");

    /* =========================================================
       Email Verification (Google)
       ========================================================= */

    const [isVerifyingGoogle, setIsVerifyingGoogle] =
        useState(false);

    const [googleError, setGoogleError] =
        useState<string | null>(null);

    /* =========================================================
       Agreement Signing OTP
       ========================================================= */

    const [signOtp, setSignOtp] = useState<string[]>(
        Array(OTP_LENGTH).fill("")
    );

    const [signResendTimer, setSignResendTimer] =
        useState(0);

    const [isRequestingSignOtp, setIsRequestingSignOtp] =
        useState(false);

    const [signOtpError, setSignOtpError] =
        useState<string | null>(null);

    const [signResendError, setSignResendError] =
        useState<string | null>(null);

    const [pdfViewError, setPdfViewError] =
        useState<string | null>(null);

    /*
     * IMPORTANT:
     *
     * This explicitly tracks whether VERIFY_OTP succeeded.
     *
     * We do NOT infer this from the error message anymore.
     *
     * false -> OTP still needs to be submitted
     * true  -> OTP was already accepted, only status needs checking
     */
    const [otpAccepted, setOtpAccepted] =
        useState(false);

    const [isConfirmingStatus, setIsConfirmingStatus] =
        useState(false);

    const signOtpRefs =
        useRef<(HTMLInputElement | null)[]>([]);

    /*
     * Tracks the last sign OTP value that was
     * auto-submitted, so the auto-verify effect
     * doesn't resubmit the same value repeatedly.
     */
    const lastAutoSubmittedSignOtpRef =
        useRef<string>("");

    useEffect(() => {

        if (step !== "otpVerify") {
            return;
        }

        const frame =
            requestAnimationFrame(() => {

                signOtpRefs
                    .current[0]
                    ?.focus();

            });

        return () => {

            cancelAnimationFrame(
                frame
            );

        };

    }, [step]);

    /* =========================================================
       Derived state
       ========================================================= */

    const isEmailOtpComplete =
        emailOtp.every((digit) => digit !== "");

    const isSignOtpComplete =
        signOtp.every((digit) => digit !== "");

    /* =========================================================
       Completion timeout cleanup
       ========================================================= */

    const doneTimeoutRef =
        useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (doneTimeoutRef.current) {
                clearTimeout(doneTimeoutRef.current);
                doneTimeoutRef.current = null;
            }
        };
    }, []);

    /* =========================================================
       Populate email from journey
       ========================================================= */

    useEffect(() => {
        if (journey?.customerEmail) {
            setEmail(journey.customerEmail);
        }
    }, [journey?.customerEmail]);

    /* =========================================================
   Resume: fetch PDF if we skipped straight to pdfView
   ========================================================= */

    const hasFetchedResumePdfRef =
        useRef(false);

    useEffect(() => {
        if (step !== "pdfView") return;
        if (agreementPdf) return;
        if (hasFetchedResumePdfRef.current) return;

        hasFetchedResumePdfRef.current = true;

        setIsLoadingAgreement(true);
        setStep("emailVerifying");
        handleGenerateAgreement();
    }, []);

    /* =========================================================
       Countdown timers
       ========================================================= */

    useEffect(() => {
        if (emailResendTimer <= 0) return;

        const interval = setInterval(() => {
            setEmailResendTimer((prev) =>
                prev <= 1 ? 0 : prev - 1
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [emailResendTimer]);

    useEffect(() => {
        if (signResendTimer <= 0) return;

        const interval = setInterval(() => {
            setSignResendTimer((prev) =>
                prev <= 1 ? 0 : prev - 1
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [signResendTimer]);

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        return `${minutes}:${remainingSeconds
            .toString()
            .padStart(2, "0")}`;
    };

    /* =========================================================
       Email OTP
       ========================================================= */

    const handleSendEmailOtp = async () => {
        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setEmailEntryError(
                "Please enter a valid email address."
            );
            return;
        }

        setEmailEntryError(null);
        setEmailOtpError(null);
        setEmailResendError(null);
        setIsSendingEmailOtp(true);

        try {
            const response =
                await sendEmailOTP(trimmedEmail);

            setEmailOtpHash(
                response.data.otpHash
            );

            setEmailOtp(
                Array(OTP_LENGTH).fill("")
            );

            lastAutoSubmittedEmailOtpRef.current = "";

            setEmailResendTimer(
                RESEND_WAIT_SECONDS
            );

            /*
             * Lock the email field once an OTP
             * has been requested for it.
             */
            setIsEmailLocked(true);

            setStep("emailOtp");

            emailOtpRefs.current[0]?.focus();
        } catch (error) {
            console.error(
                "Failed to send email OTP:",
                error
            );

            setEmailEntryError(
                getErrorMessage(
                    error,
                    "Couldn't send the OTP. Please try again."
                )
            );
        } finally {
            setIsSendingEmailOtp(false);
        }
    };

    const handleResendEmailOtp = async () => {
        if (emailResendTimer > 0) return;

        setEmailResendError(null);
        setEmailOtpError(null);

        try {
            const response =
                await sendEmailOTP(email.trim());

            setEmailOtpHash(
                response.data.otpHash
            );

            setEmailOtp(
                Array(OTP_LENGTH).fill("")
            );

            lastAutoSubmittedEmailOtpRef.current = "";

            setEmailResendTimer(
                RESEND_WAIT_SECONDS
            );

            emailOtpRefs.current[0]?.focus();
        } catch (error) {
            console.error(
                "Failed to resend email OTP:",
                error
            );

            setEmailResendError(
                getErrorMessage(
                    error,
                    "Couldn't resend the OTP. Please try again."
                )
            );
        }
    };

    const handleEmailOtpChange = (
        value: string,
        index: number
    ) => {
        if (!/^[0-9]?$/.test(value)) {
            return;
        }

        const next = [...emailOtp];
        next[index] = value;

        setEmailOtp(next);

        lastAutoSubmittedEmailOtpRef.current = "";

        if (value && index < OTP_LENGTH - 1) {
            emailOtpRefs.current[
                index + 1
            ]?.focus();
        }
    };

    const handleEmailOtpKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (
            event.key === "Backspace" &&
            !emailOtp[index] &&
            index > 0
        ) {
            emailOtpRefs.current[
                index - 1
            ]?.focus();
        }
    };

    const handleEmailOtpPaste = (
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {
        const pasted = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, OTP_LENGTH);

        if (!pasted) return;

        event.preventDefault();

        const next = Array(OTP_LENGTH).fill("");

        for (
            let index = 0;
            index < pasted.length;
            index++
        ) {
            next[index] = pasted[index];
        }

        setEmailOtp(next);

        lastAutoSubmittedEmailOtpRef.current = "";

        const focusIndex = Math.min(
            pasted.length,
            OTP_LENGTH - 1
        );

        emailOtpRefs.current[
            focusIndex
        ]?.focus();
    };

    const handleVerifyEmail = async () => {
        if (
            !isEmailOtpComplete ||
            !emailOtpHash
        ) {
            return;
        }

        setEmailOtpError(null);
        setStep("emailVerifying");
        setIsLoadingAgreement(false);
        setAgreementLoadError(false);

        try {
            await verifyEmailOTP({
                email: email.trim(),
                otp: emailOtp.join(""),
                otpHash: emailOtpHash,
            });

            setIsLoadingAgreement(true);

            await handleGenerateAgreement();
        } catch (error) {
            console.error(
                "Failed to verify email OTP:",
                error
            );

            lastAutoSubmittedEmailOtpRef.current = "";

            setEmailOtpError(
                getErrorMessage(
                    error,
                    "That code didn't match. Please try again."
                )
            );

            setStep("emailOtp");
        }
    };

    /*
     * =========================================================
     * AUTO VERIFY EMAIL OTP ON COMPLETE
     * =========================================================
     */
    useEffect(() => {

        const entered = emailOtp.join("");

        if (entered.length !== OTP_LENGTH) {
            return;
        }

        if (step !== "emailOtp") {
            return;
        }

        if (!emailOtpHash) {
            return;
        }

        if (
            lastAutoSubmittedEmailOtpRef.current ===
            entered
        ) {
            return;
        }

        lastAutoSubmittedEmailOtpRef.current =
            entered;

        handleVerifyEmail();

    }, [emailOtp, step, emailOtpHash]);

    /* =========================================================
       Email Verification via Google
       ========================================================= */

    const handleGoogleSuccess = async (
        credentialResponse: CredentialResponse
    ) => {
        if (!credentialResponse.credential) {
            setGoogleError(
                "Google sign-in failed. Please try again."
            );
            return;
        }

        setGoogleError(null);
        setEmailEntryError(null);
        setStep("emailVerifying");
        setIsLoadingAgreement(false);
        setAgreementLoadError(false);
        setIsVerifyingGoogle(true);

        try {
            const response = await verifyGoogleEmail(
                credentialResponse.credential
            );

            if (response.data.email) {
                setEmail(response.data.email);
            }

            setIsLoadingAgreement(true);

            await handleGenerateAgreement();
        } catch (error) {
            console.error(
                "Failed to verify Google email:",
                error
            );

            setGoogleError(
                getErrorMessage(
                    error,
                    "Couldn't verify your Google account. Please try again."
                )
            );

            setStep("emailEntry");
        } finally {
            setIsVerifyingGoogle(false);
        }
    };

    const handleGoogleError = () => {
        setGoogleError(
            "Google sign-in failed. Please try again."
        );
    };

    /* =========================================================
       Agreement generation
       ========================================================= */

    const handleGenerateAgreement =
        async () => {
            if (isLoadingAgreement) {
                return;
            }

            try {
                setAgreementLoadError(false);
                setPdfViewError(null);
                setIsLoadingAgreement(true);

                const response =
                    await journeyAction({
                        subStep: "AGREEMENT",
                        action: "GENERATE",
                        payload: {},
                    });

                const metadata =
                    response.data.metadata;

                if (!metadata?.pdf) {
                    throw new Error(
                        "Agreement PDF was not returned."
                    );
                }

                setAgreementPdf(metadata.pdf);

                setAgreementLoadError(false);
                setStep("pdfView");
            } catch (error) {
                console.error(
                    "Failed to generate agreement:",
                    error
                );

                setAgreementLoadError(true);
                setStep("emailVerifying");
            } finally {
                setIsLoadingAgreement(false);
            }
        };

    /* =========================================================
       Request Sign OTP
       ========================================================= */

    const handleRequestSignOtp =
        async () => {
            if (isRequestingSignOtp) {
                return;
            }

            setPdfViewError(null);
            setSignOtpError(null);
            setSignResendError(null);

            setIsRequestingSignOtp(true);

            try {
                await journeyAction({
                    subStep: "AGREEMENT",
                    action: "SEND_OTP",
                    payload: {},
                });

                /*
                 * New OTP means the previous OTP is no longer
                 * considered accepted.
                 */
                setOtpAccepted(false);

                setSignOtp(
                    Array(OTP_LENGTH).fill("")
                );

                lastAutoSubmittedSignOtpRef.current = "";

                setSignResendTimer(
                    RESEND_WAIT_SECONDS
                );

                setStep("otpVerify");

                signOtpRefs.current[0]?.focus();
            } catch (error) {
                console.error(
                    "Failed to send agreement OTP:",
                    error
                );

                setPdfViewError(
                    getErrorMessage(
                        error,
                        "Couldn't send the sign OTP. Please try again."
                    )
                );
            } finally {
                setIsRequestingSignOtp(false);
            }
        };

    /* =========================================================
       Resend Sign OTP
       ========================================================= */

    const handleResendSignOtp =
        async () => {
            if (signResendTimer > 0) {
                return;
            }

            setSignResendError(null);
            setSignOtpError(null);

            try {
                await journeyAction({
                    subStep: "AGREEMENT",
                    action: "SEND_OTP",
                    payload: {},
                });

                /*
                 * The old OTP is no longer usable.
                 */
                setOtpAccepted(false);

                setSignOtp(
                    Array(OTP_LENGTH).fill("")
                );

                lastAutoSubmittedSignOtpRef.current = "";

                setSignResendTimer(
                    RESEND_WAIT_SECONDS
                );

                signOtpRefs.current[0]?.focus();
            } catch (error) {
                console.error(
                    "Failed to resend agreement OTP:",
                    error
                );

                setSignResendError(
                    getErrorMessage(
                        error,
                        "Couldn't resend the OTP. Please try again."
                    )
                );
            }
        };

    /* =========================================================
       Sign OTP input
       ========================================================= */

    const handleSignOtpChange = (
        value: string,
        index: number
    ) => {
        if (!/^[0-9]?$/.test(value)) {
            return;
        }

        const next = [...signOtp];
        next[index] = value;

        setSignOtp(next);

        lastAutoSubmittedSignOtpRef.current = "";

        if (value && index < OTP_LENGTH - 1) {
            signOtpRefs.current[
                index + 1
            ]?.focus();
        }
    };

    const handleSignOtpKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (
            event.key === "Backspace" &&
            !signOtp[index] &&
            index > 0
        ) {
            signOtpRefs.current[
                index - 1
            ]?.focus();
        }
    };

    const handleSignOtpPaste = (
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {
        const pasted = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, OTP_LENGTH);

        if (!pasted) return;

        event.preventDefault();

        const next = Array(OTP_LENGTH).fill("");

        for (
            let index = 0;
            index < pasted.length;
            index++
        ) {
            next[index] = pasted[index];
        }

        setSignOtp(next);

        lastAutoSubmittedSignOtpRef.current = "";

        const focusIndex = Math.min(
            pasted.length,
            OTP_LENGTH - 1
        );

        signOtpRefs.current[
            focusIndex
        ]?.focus();
    };

    /* =========================================================
       Confirm agreement status
       ========================================================= */

    const confirmAgreementStatus =
        async () => {
            if (isConfirmingStatus) {
                return;
            }

            setIsConfirmingStatus(true);
            setSignOtpError(null);

            try {
                const statusResponse =
                    await journeyStatus(
                        "AGREEMENT"
                    );

                const latestJourney =
                    statusResponse.data.journey;

                dispatch(
                    setJourney(latestJourney)
                );

                const agreementStatus =
                    statusResponse.data.metadata
                        ?.agreementStatus;

                const isCompleted =
                    agreementStatus ===
                    "Accepted" ||
                    latestJourney?.steps
                        ?.AGREEMENT?.status ===
                    "COMPLETED";

                if (isCompleted) {
                    setStep("signed");

                    doneTimeoutRef.current =
                        setTimeout(() => {
                            onDone();
                        }, 1300);

                    return;
                }

                setSignOtpError(
                    "Your code was verified, but the agreement is not marked as signed yet. Please check again."
                );
            } catch (error) {
                console.error(
                    "Failed to confirm agreement status:",
                    error
                );

                setSignOtpError(
                    getErrorMessage(
                        error,
                        "Your code was verified, but we couldn't confirm your signature yet. Please check again."
                    )
                );
            } finally {
                setIsConfirmingStatus(false);
            }
        };

    /* =========================================================
       Verify + Sign
       ========================================================= */

    const handleConfirmSign =
        async () => {
            if (
                !isSignOtpComplete ||
                otpAccepted ||
                isConfirmingStatus
            ) {
                return;
            }

            setSignOtpError(null);
            setStep("verifying");

            try {
                await journeyAction({
                    subStep: "AGREEMENT",
                    action: "VERIFY_OTP",
                    payload: {
                        otp: signOtp.join(""),
                    },
                });

                /*
                 * IMPORTANT:
                 *
                 * From this point onward the OTP has been
                 * accepted/consumed by the backend.
                 */
                setOtpAccepted(true);

                /*
                 * Immediately check the latest status.
                 */
                const statusResponse =
                    await journeyStatus(
                        "AGREEMENT"
                    );

                const latestJourney =
                    statusResponse.data.journey;

                dispatch(
                    setJourney(latestJourney)
                );

                const agreementStatus =
                    statusResponse.data.metadata
                        ?.agreementStatus;

                const isCompleted =
                    agreementStatus ===
                    "Accepted" ||
                    latestJourney?.steps
                        ?.AGREEMENT?.status ===
                    "COMPLETED";

                if (!isCompleted) {
                    throw new Error(
                        "Agreement signing could not be confirmed."
                    );
                }

                setStep("signed");

                doneTimeoutRef.current =
                    setTimeout(() => {
                        onDone();
                    }, 1300);
            } catch (error) {
                console.error(
                    "Failed to sign agreement:",
                    error
                );

                /*
                 * If OTP was already accepted, NEVER ask the
                 * user to submit the same OTP again.
                 */
                if (otpAccepted) {
                    setStep("otpVerify");

                    setSignOtpError(
                        "Your code was verified, but we couldn't confirm your signature yet. Please check the status again."
                    );
                } else {
                    setStep("otpVerify");

                    // Clear the invalid OTP so the user re-enters it — don't
                    // reset lastAutoSubmittedSignOtpRef here, since the digits
                    // are cleared anyway (entered.length !== OTP_LENGTH stops
                    // the auto-verify effect from firing on its own).
                    setSignOtp(Array(OTP_LENGTH).fill(""));

                    setSignOtpError(
                        getErrorMessage(error, "The OTP could not be verified. Please try again.")
                    );

                    requestAnimationFrame(() => {
                        signOtpRefs.current[0]?.focus();
                    });
                }
            }
        };

    /* =========================================================
       Main Sign button handler
       ========================================================= */

    const handleConfirmSignOrRecheck =
        async () => {
            if (isConfirmingStatus) {
                return;
            }

            /*
             * OTP already accepted:
             * only poll/check status.
             */
            if (otpAccepted) {
                await confirmAgreementStatus();
                return;
            }

            /*
             * OTP has not yet been accepted:
             * submit it.
             */
            await handleConfirmSign();
        };

    /*
     * =========================================================
     * AUTO VERIFY SIGN OTP ON COMPLETE
     * =========================================================
     */
    useEffect(() => {

        const entered = signOtp.join("");

        if (entered.length !== OTP_LENGTH) {
            return;
        }

        if (step !== "otpVerify") {
            return;
        }

        if (otpAccepted) {
            return;
        }

        if (isConfirmingStatus) {
            return;
        }

        if (
            lastAutoSubmittedSignOtpRef.current ===
            entered
        ) {
            return;
        }

        lastAutoSubmittedSignOtpRef.current =
            entered;

        handleConfirmSignOrRecheck();

    }, [signOtp, step, otpAccepted, isConfirmingStatus]);

    /* =========================================================
       Back navigation
       ========================================================= */

    const handleBack = () => {
        if (step === "emailEntry") {
            return onBack();
        }

        if (step === "emailOtp") {
            /*
             * Let the user edit the email again
             * if they go back from the OTP screen.
             */
            setIsEmailLocked(false);
            return setStep("emailEntry");
        }

        if (step === "pdfView") {
            /*
             * Email is already verified at this point — going
             * "back" should not force re-verification. Return
             * to the dashboard instead.
             */
            return onBack();
        }

        if (step === "otpVerify") {
            if (otpAccepted) {
                return;
            }

            return setStep("pdfView");
        }
    };

    /* =========================================================
       Email field edit toggle
       ========================================================= */

    const handleEditEmail = () => {
        setIsEmailLocked(false);
        setEmailEntryError(null);

        requestAnimationFrame(() => {
            emailInputRef.current?.focus();
        });
    };

    const handleEmailChange = (
        value: string
    ) => {
        setEmail(value);

        if (emailEntryError) {
            setEmailEntryError(null);
        }
    };

    const showHeader =
        step === "emailEntry" ||
        step === "emailOtp" ||
        step === "pdfView" ||
        step === "otpVerify";

    /* =========================================================
       UI
       ========================================================= */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div className="absolute -left-20 -top-24 h-64 w-64 rounded-full bg-purple-200 opacity-20 blur-3xl" />

                <div className="absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl" />

                <div className="relative z-10 flex h-full flex-col">

                    {/* Header */}

                    {showHeader && (
                        <div className="flex shrink-0 items-center justify-between px-6 pt-6">

                            <div className="flex items-center gap-3">

                                <button
                                    onClick={handleBack}
                                    disabled={
                                        step === "otpVerify" &&
                                        otpAccepted
                                    }
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-gray-600 shadow-sm transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronLeft className="h-4.5 w-4.5" />
                                </button>

                                <p className="text-base font-bold text-gray-900">
                                    {STEP_TITLES[step]}
                                </p>

                            </div>

                            <img
                                src="/CleverPeLogos/CleverPE_Black_icon.svg"
                                alt="CleverPe"
                                className="-mt-3 h-6 w-6 opacity-90"
                            />

                        </div>
                    )}

                    {/* =================================================
                        EMAIL ENTRY
                    ================================================= */}

                    {step === "emailEntry" && (
                        <div className="flex flex-1 flex-col px-6 pb-4 pt-6">

                            <p className="mt-1 text-[13px] leading-snug text-gray-500">
                                We'll verify your email before you review and sign the agreement.
                            </p>

                            <div className="relative mt-5">

                                <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">

                                    <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />

                                    <div className="relative z-10 flex items-center gap-3">

                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
                                            <Mail
                                                className="h-5.5 w-5.5"
                                                strokeWidth={2}
                                            />
                                        </div>

                                        <div>
                                            <h3 className="text-[14px] font-semibold leading-tight text-gray-900">
                                                Email Address
                                            </h3>

                                            <p className="text-[12px] leading-tight text-gray-500">
                                                Linked to your profile
                                            </p>
                                        </div>

                                    </div>

                                    {/*
                                        Email input row.

                                        - Editable + pencil icon while
                                          not locked.
                                        - "Send OTP" pill lives in the
                                          same row as the primary action
                                          for this field.
                                    */}

                                    <div className="relative z-10 mt-3">

                                        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3">

                                            <input
                                                ref={emailInputRef}
                                                value={email}
                                                onChange={(event) =>
                                                    handleEmailChange(
                                                        event.target.value
                                                    )
                                                }
                                                type="email"
                                                autoComplete="email"
                                                readOnly={isEmailLocked}
                                                disabled={
                                                    isSendingEmailOtp ||
                                                    isVerifyingGoogle
                                                }
                                                placeholder="Enter your email"
                                                className="w-full min-w-0 bg-transparent text-[14px] font-medium text-gray-600 outline-none placeholder:font-normal placeholder:text-gray-400 disabled:cursor-not-allowed"
                                            />

                                            {isEmailLocked ? (
                                                <button
                                                    type="button"
                                                    onClick={handleEditEmail}
                                                    disabled={
                                                        isSendingEmailOtp ||
                                                        isVerifyingGoogle
                                                    }
                                                    aria-label="Edit email address"
                                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-indigo-100/60 hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleSendEmailOtp}
                                                    disabled={
                                                        !email.trim() ||
                                                        isSendingEmailOtp ||
                                                        isVerifyingGoogle
                                                    }
                                                    className="shrink-0 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 px-3.5 py-1.5 text-[11.5px] font-semibold text-white transition disabled:cursor-not-allowed disabled:from-indigo-300 disabled:to-blue-200"
                                                >
                                                    {isSendingEmailOtp ? (
                                                        <span className="flex items-center gap-1.5">
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                            Sending...
                                                        </span>
                                                    ) : (
                                                        "Send OTP"
                                                    )}
                                                </button>
                                            )}

                                        </div>

                                    </div>

                                    <InlineError
                                        message={
                                            emailEntryError
                                        }
                                    />

                                </div>

                            </div>

                            <div className="mt-5 mb-4 flex items-center gap-3">
                                <div className="h-px flex-1 bg-gray-200" />
                                <span className="text-[11px] font-medium text-gray-400">
                                    OR
                                </span>
                                <div className="h-px flex-1 bg-gray-200" />
                            </div>

                            <div className="mb-1 flex justify-center">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                    width="100%"
                                    shape="pill"
                                    text="continue_with"
                                />
                            </div>

                            <InlineError message={googleError} />

                            <div className="flex-1" />

                            <div className="mt-3">
                                <PoweredByFooter />
                            </div>

                        </div>
                    )}

                    {/* =================================================
                        EMAIL OTP
                    ================================================= */}

                    {step === "emailOtp" && (
                        <div className="flex flex-1 flex-col px-6 pb-4 pt-5">

                            <div className="mt-2">

                                <p className="text-[13px] leading-snug text-gray-500">
                                    Enter the 6-digit code sent to{" "}
                                    <span className="font-semibold text-indigo-600">
                                        {email}
                                    </span>
                                </p>

                            </div>

                            <div className="mt-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">

                                <div className="flex justify-center gap-2">

                                    {emailOtp.map(
                                        (digit, index) => (
                                            <input
                                                key={index}
                                                ref={(element) => {
                                                    emailOtpRefs.current[
                                                        index
                                                    ] = element;
                                                }}
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete={
                                                    index === 0
                                                        ? "one-time-code"
                                                        : "off"
                                                }
                                                maxLength={1}
                                                value={digit}
                                                onChange={(event) => {
                                                    handleEmailOtpChange(
                                                        event
                                                            .target
                                                            .value,
                                                        index
                                                    );

                                                    if (
                                                        emailOtpError
                                                    ) {
                                                        setEmailOtpError(
                                                            null
                                                        );
                                                    }
                                                }}
                                                onKeyDown={(event) =>
                                                    handleEmailOtpKeyDown(
                                                        event,
                                                        index
                                                    )
                                                }
                                                onPaste={
                                                    handleEmailOtpPaste
                                                }
                                                className="h-12 w-10 rounded-xl border border-gray-200 bg-white text-center text-lg font-medium text-gray-900 outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 sm:h-14 sm:w-11"
                                            />
                                        )
                                    )}

                                </div>

                            </div>

                            <InlineError
                                message={emailOtpError}
                            />

                            <div className="mt-6 flex items-center justify-center gap-2 text-sm">

                                <span className="text-gray-500">
                                    Didn't receive the code?
                                </span>

                                <button
                                    onClick={
                                        handleResendEmailOtp
                                    }
                                    disabled={
                                        emailResendTimer > 0
                                    }
                                    className="font-semibold text-indigo-600 transition hover:text-indigo-700 disabled:text-gray-400 disabled:opacity-50"
                                >
                                    {emailResendTimer > 0
                                        ? `Resend OTP in ${formatTime(
                                            emailResendTimer
                                        )}`
                                        : "Resend OTP"}
                                </button>

                            </div>

                            <InlineError
                                message={
                                    emailResendError
                                }
                            />

                            <div className="flex-1" />

                            <button
                                onClick={
                                    handleVerifyEmail
                                }
                                disabled={
                                    !isEmailOtpComplete
                                }
                                className="mb-4 w-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-3 text-m font-semibold text-white disabled:opacity-50"
                            >
                                Verify Email
                            </button>

                            <PoweredByFooter />

                        </div>
                    )}

                    {/* =================================================
                        EMAIL VERIFYING / AGREEMENT LOADING
                    ================================================= */}

                    {step === "emailVerifying" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white px-8 text-center">

                            {!agreementLoadError ? (
                                <>
                                    <div className="h-36 w-36">

                                        <Lottie
                                            animationData={
                                                isLoadingAgreement
                                                    ? contractAnimation
                                                    : Email
                                            }
                                            loop
                                            autoplay
                                        />

                                    </div>

                                    <p className="mt-1 text-[13.5px] font-semibold text-gray-800">
                                        {isLoadingAgreement
                                            ? "Loading your agreement"
                                            : isVerifyingGoogle
                                                ? "Verifying your Google account"
                                                : "Verifying your email"}
                                    </p>

                                    <p className="mt-1 text-[11.5px] leading-snug text-gray-500">
                                        {isLoadingAgreement
                                            ? "Please wait while we prepare your agreement"
                                            : "Please wait a moment"}
                                    </p>
                                </>
                            ) : (
                                <>
                                    {/* Top-right logo */}
                                    <img
                                        src="/CleverPeLogos/CleverPE_Black_icon.svg"
                                        alt="CleverPe"
                                        className="absolute right-5 top-5 h-6 w-6 opacity-90"
                                    />

                                    {/* Center content */}
                                    <div className="flex flex-1 flex-col items-center justify-center text-center">
                                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-50">
                                            <Loader2 className="h-9 w-9 text-indigo-500" />
                                        </div>

                                        <p className="mt-5 text-[13.5px] font-semibold text-gray-800">
                                            We’re having trouble loading your loan agreement right now
                                        </p>

                                        <p className="mt-1 max-w-[270px] text-[11.5px] leading-snug text-gray-500">
                                            Please try again to continue with e-signing.
                                        </p>
                                    </div>

                                    {/* Bottom button */}
                                    <button
                                        onClick={handleGenerateAgreement}
                                        disabled={isLoadingAgreement}
                                        className="mt-auto mb-10 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 px-7 py-3 text-[13px] font-semibold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {isLoadingAgreement ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading...
                                            </>
                                        ) : (
                                            "Retry"
                                        )}
                                    </button>
                                </>
                            )}

                            <PoweredByFooter className="absolute bottom-3" />

                        </div>
                    )}

                    {/* =================================================
                        PDF VIEW
                    ================================================= */}

                    {step === "pdfView" && (
                        <div className="flex min-h-0 flex-1 flex-col px-6 pb-4 pt-4">

                            <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                                <AgreementPdfViewer
                                    base64={agreementPdf}
                                    onError={() =>
                                        setPdfViewError(
                                            "Unable to load the agreement. Please go back and try again."
                                        )
                                    }
                                />
                            </div>

                            <InlineError message={pdfViewError} />

                            <div className="shrink-0 pt-4">
                                <button
                                    onClick={handleRequestSignOtp}
                                    disabled={isRequestingSignOtp}
                                    className="mb-4 w-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-3 text-m font-semibold text-white disabled:cursor-not-allowed disabled:from-indigo-300 disabled:to-blue-200"
                                >
                                    {isRequestingSignOtp ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending OTP...
                                        </span>
                                    ) : (
                                        "Sign Agreement"
                                    )}
                                </button>

                                <PoweredByFooter />
                            </div>

                        </div>
                    )}

                    {/* =================================================
                        SIGN OTP
                    ================================================= */}

                    {step === "otpVerify" && (
                        <div className="flex flex-1 flex-col px-6 pb-4 pt-5">

                            <div className="mt-2">

                                <p className="text-[13px] leading-snug text-gray-500">
                                    Enter the 6-digit code sent to{" "}
                                    <span className="font-semibold text-indigo-600">
                                        {customerMobileMasked}
                                    </span>
                                </p>

                            </div>

                            <div className="mt-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">

                                <div className="flex justify-center gap-2">

                                    {signOtp.map(
                                        (digit, index) => (
                                            <input
                                                key={index}
                                                ref={(element) => {
                                                    signOtpRefs.current[
                                                        index
                                                    ] = element;
                                                }}
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete={
                                                    index === 0
                                                        ? "one-time-code"
                                                        : "off"
                                                }
                                                maxLength={1}
                                                value={digit}
                                                disabled={
                                                    otpAccepted
                                                }
                                                onChange={(event) => {
                                                    handleSignOtpChange(
                                                        event
                                                            .target
                                                            .value,
                                                        index
                                                    );

                                                    if (
                                                        signOtpError
                                                    ) {
                                                        setSignOtpError(
                                                            null
                                                        );
                                                    }
                                                }}
                                                onKeyDown={(event) =>
                                                    handleSignOtpKeyDown(
                                                        event,
                                                        index
                                                    )
                                                }
                                                onPaste={
                                                    handleSignOtpPaste
                                                }
                                                className="h-12 w-10 rounded-xl border border-gray-200 bg-white text-center text-lg font-medium text-gray-900 outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50 disabled:text-gray-400 sm:h-14 sm:w-11"
                                            />
                                        )
                                    )}

                                </div>

                            </div>

                            <InlineError
                                message={signOtpError}
                            />

                            {!otpAccepted && (
                                <>
                                    <div className="mt-5 text-center text-[12.5px]">

                                        <span className="text-gray-500">
                                            Didn't receive the code?{" "}
                                        </span>

                                        <button
                                            onClick={
                                                handleResendSignOtp
                                            }
                                            disabled={
                                                signResendTimer > 0
                                            }
                                            className={`font-semibold transition ${signResendTimer > 0
                                                ? "text-gray-400"
                                                : "text-indigo-600"
                                                }`}
                                        >
                                            {signResendTimer > 0
                                                ? `Resend OTP in ${formatTime(
                                                    signResendTimer
                                                )}`
                                                : "Resend OTP"}
                                        </button>

                                    </div>

                                    <InlineError
                                        message={
                                            signResendError
                                        }
                                    />
                                </>
                            )}

                            <div className="flex-1" />

                            <button
                                onClick={
                                    handleConfirmSignOrRecheck
                                }
                                disabled={
                                    isConfirmingStatus ||
                                    (!otpAccepted &&
                                        !isSignOtpComplete)
                                }
                                className="mb-4 w-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-3 text-m font-semibold text-white disabled:opacity-50"
                            >
                                {isConfirmingStatus ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Checking status...
                                    </span>
                                ) : otpAccepted ? (
                                    "Check Status"
                                ) : (
                                    "Verify & Sign"
                                )}
                            </button>

                            <PoweredByFooter />

                        </div>
                    )}

                    {/* =================================================
                        VERIFYING
                    ================================================= */}

                    {step === "verifying" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white px-8 text-center">

                            <div className="h-36 w-36">
                                <Lottie
                                    animationData={
                                        contractAnimation
                                    }
                                    loop
                                    autoplay
                                />
                            </div>

                            <p className="mt-1 text-[13.5px] font-semibold text-gray-800">
                                Verifying &amp; signing your agreement
                            </p>

                            <p className="mt-1 text-[11.5px] leading-snug text-gray-500">
                                Please wait while we execute the e-sign
                            </p>

                            <PoweredByFooter className="absolute bottom-6" />

                        </div>
                    )}

                    {/* =================================================
                        SIGNED
                    ================================================= */}

                    {step === "signed" && (
                        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white px-8 text-center">

                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 shadow-lg animate-[popIn_0.4s_ease-out]">

                                <CheckCircle2 className="h-10 w-10 text-white" />

                            </div>

                            <p className="mt-4 bg-gradient-to-r from-emerald-500 to-emerald-400 bg-clip-text text-[14.5px] font-semibold text-transparent">
                                Agreement Signed Successfully
                            </p>

                            <p className="mt-1 text-[12px] leading-snug text-gray-500">
                                Your loan agreement has been e-signed and recorded.
                            </p>

                            <PoweredByFooter className="absolute bottom-6" />

                            <style>
                                {`
                                    @keyframes popIn {
                                        0% {
                                            transform: scale(0.4);
                                            opacity: 0;
                                        }

                                        100% {
                                            transform: scale(1);
                                            opacity: 1;
                                        }
                                    }
                                `}
                            </style>

                        </div>
                    )}

                </div>
            </div>
        </MobileFrame>
    );
};

export default AgreementSigning;